// Student-first gate. Shown before seat selection: the veli picks their öğrenci
// (student) from the davetiye list. No phone here — phone/e-mail/note are
// collected in the checkout modal after seats are chosen. The reservation is
// stored under the chosen student's name and they drop out of the list.

import { api } from '@/lib/api.ts';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export type SelectedStudent = { id: string; name: string };

type Props = {
  eventId: string;
  onConfirm: (student: SelectedStudent) => void;
};

export function StudentGate({ eventId, onConfirm }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SelectedStudent[]>([]);
  const [selected, setSelected] = useState<SelectedStudent | null>(null);
  const [searching, setSearching] = useState(false);

  // Debounced lookup: fire from 2 chars (server applies the precise davetiye
  // rule). Skip while a student is already selected and the box still shows it.
  useEffect(() => {
    const q = query.trim();
    if (selected && q === selected.name) {
      setResults([]);
      return;
    }
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api<{ students: SelectedStudent[] }>(
          `/events/${eventId}/students?q=${encodeURIComponent(q)}`,
        );
        if (!cancelled) setResults(res.students);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, selected, eventId]);

  const pick = (s: SelectedStudent) => {
    setSelected(s);
    setQuery(s.name);
    setResults([]);
    setError(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      setError('Lütfen listeden öğrenci adını seçin.');
      return;
    }
    onConfirm(selected);
  };

  const showResults = !selected && results.length > 0;

  return (
    <div
      className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="student-gate-title"
    >
      <motion.form
        initial={{ y: 24, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 30 }}
        onSubmit={submit}
        className="modal-card relative w-full max-w-[460px] overflow-hidden rounded-2xl"
      >
        <header className="border-b border-border px-8 pb-6 pt-7">
          <div className="kicker mb-2 text-accent">Başlamadan önce</div>
          <h2 id="student-gate-title" className="display text-2xl font-light tracking-tight">
            Öğrenciyi seçin
          </h2>
          <p className="mt-2 text-[13px] text-text-muted">
            Koltuk seçimine başlamadan önce öğrencinizin adını listeden seçin. İletişim
            bilgilerinizi koltukları seçtikten sonra alacağız.
          </p>
        </header>

        <div className="px-8 py-7">
          <div className="relative block">
            <span className="kicker mb-1.5 block">Öğrenci adı</span>
            <input
              type="text"
              value={query}
              // biome-ignore lint/a11y/noAutofocus: gate is a focused modal task; autofocus is the expected UX
              autoFocus
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                if (selected) setSelected(null);
              }}
              className="input"
              placeholder="Öğrencinin adını yazın…"
              aria-expanded={showResults}
            />
            {selected && (
              <span className="pointer-events-none absolute right-3 top-[34px] text-[12px] text-success">
                ✓ seçildi
              </span>
            )}
            {searching && !selected && query.trim().length >= 2 && (
              <p className="mt-1.5 text-[11px] text-text-faint">Aranıyor…</p>
            )}
            {showResults && (
              <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-border bg-bg-elevated py-1 shadow-lg">
                {results.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => pick(s)}
                      className="block w-full px-4 py-2.5 text-left text-[14px] text-text transition-colors hover:bg-surface"
                    >
                      {s.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!selected && !searching && query.trim().length >= 3 && results.length === 0 && (
              <p className="mt-1.5 text-[11px] text-text-faint">
                Bu isimle uygun öğrenci bulunamadı (zaten rezervasyon yapılmış olabilir).
              </p>
            )}
          </div>
          {error && <p className="mt-2 text-[12px] text-accent">{error}</p>}
        </div>

        <footer className="flex items-center justify-end border-t border-border px-8 py-5">
          <button
            type="submit"
            className="group inline-flex items-center gap-3 rounded-md bg-accent px-6 py-3 text-[13px] font-medium tracking-wide text-white transition-all hover:bg-accent-hover disabled:opacity-50"
          >
            Devam et
            <span
              aria-hidden
              className="text-[14px] transition-transform group-hover:translate-x-0.5"
            >
              →
            </span>
          </button>
        </footer>
      </motion.form>
    </div>
  );
}
